import { useEffect, useContext, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import ModalOverlay from 'builder/components/ModalOverlay'
import { useI18n } from 'builder/hooks/useI18n'
import { SnackBar } from 'builder/components/SnackBarV2'
import { SnackBarTypes } from 'builder/components/SnackBarV2/types'
import { SnackBarContext } from 'builder/components/SnackBarV2/SnackBarContext'
import { FetchStatuses } from 'builder/modules/constants'
import {
  actions,
  AutoTailoringFromPublicJobBoardStep,
  selectors,
} from 'builder/modules/resumeEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { TailoringFromPublicJobBoardHeader } from 'builder/components/DocumentEditor/TailoringFromPublicJobBoard'
import { useConfig } from 'builder/hooks/useConfig'
import { useOverviewModal } from './hooks/useOverviewModal'
import { Toolbar } from './components/Toolbar/Toolbar'
import { Container, Wrapper } from './styles'
import { ApproveModal } from './components/ApproveModal/ApproveModal'
import { DocumentPreview } from './components/DocumentPreview/DocumentPreview'
import { TailoredResumeEdit } from './components/TailoredResumeEdit/TailoredResumeEdit'
import { TailoredResumeEditForPublicJobTracker } from './components/TailoredResumeEditForPublicJobTracker/TailoredResumeEditForPublicJobTracker'

export const OverviewModal = () => {
  const dispatch = useDispatch()
  const params = useParams()
  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )
  const {
    handleOpenApproveModal,
    resume,
    isApproveModalOpen,
    approveTailoredResumeStatus,
    isResumeLoading,
  } = useOverviewModal()
  const { openSnackBar } = useContext(SnackBarContext)
  const { i18n } = useI18n()
  const [paginationBottomPosition, setPaginationBottomPosition] = useState<string | null>(null)
  const isNewBuilderEnabled = useConfig()?.features.isNewBuilderEnabled

  useEffect(() => {
    if (!resume && !isResumeLoading) {
      const resumeId = Number(params.id)
      openSnackBar({
        text: i18n.t('builder.auto_tailoring.overview.error_load_resume.text'),
        type: SnackBarTypes.failure,
        action: {
          onClick: () => {
            dispatch(actions.fetchResumeRequest({ id: resumeId }))
          },
          text: i18n.t('builder.auto_tailoring.overview.error_load_resume.action_button_text'),
        },
      })
    }
  }, [
    approveTailoredResumeStatus,
    dispatch,
    i18n,
    isResumeLoading,
    openSnackBar,
    params.id,
    resume,
  ])

  useEffect(() => {
    if (approveTailoredResumeStatus === FetchStatuses.failed) {
      openSnackBar({
        text: i18n.t('builder.auto_tailoring.overview.save_error_text'),
        type: SnackBarTypes.failure,
        isDismissible: true,
        isAutoDismissible: false,
      })
    }
  }, [approveTailoredResumeStatus, i18n, openSnackBar])

  useEffect(() => {
    trackInternalEvent('see_auto_tailoring_overview_screen')
  }, [])

  useEffect(() => {
    if (autoTailoringFromPublicJobBoard.enabled) {
      dispatch(
        actions.setAutoTailoringForPublicJobBoard({
          step: AutoTailoringFromPublicJobBoardStep.step2,
        }),
      )
    }
  }, [autoTailoringFromPublicJobBoard, dispatch])

  return (
    <ModalOverlay fullScreen onClose={handleOpenApproveModal}>
      <Container data-testid="autotailoring-overview-modal">
        {autoTailoringFromPublicJobBoard.enabled ? (
          <TailoringFromPublicJobBoardHeader isNewBuilderEnabled={isNewBuilderEnabled} />
        ) : (
          <Toolbar />
        )}
        <Wrapper
          style={{ paddingTop: autoTailoringFromPublicJobBoard.enabled ? '20px' : undefined }}
        >
          <DocumentPreview
            forceLoadingState={
              autoTailoringFromPublicJobBoard.enabled &&
              autoTailoringFromPublicJobBoard.processingAutoTailoring === FetchStatuses.loading
            }
            paginationBottomPosition={paginationBottomPosition}
          />
          {autoTailoringFromPublicJobBoard.enabled ? (
            <TailoredResumeEditForPublicJobTracker
              onDrawerInitialHeightChange={setPaginationBottomPosition}
            />
          ) : (
            <TailoredResumeEdit />
          )}
        </Wrapper>
        {isApproveModalOpen && <ApproveModal />}
        <SnackBar />
      </Container>
    </ModalOverlay>
  )
}
